import get from 'lodash/get.js';
/**
 * Check if a path segment represents an array index (numeric string).
 */
export function isArrayIndex(segment) {
    return /^\d+$/.test(segment);
}
/**
 * Get the last segment from a parsed path (array of segments).
 */
export function getLastSegment(segments) {
    return segments[segments.length - 1] ?? '';
}
/**
 * Get a value at a path within an object.
 */
export function getPath(obj, path) {
    if (!path)
        return obj;
    return get(obj, path);
}
export function getParentPath(path) {
    if (!path)
        return '';
    const lastDotIndex = path.lastIndexOf('.');
    const lastBracketIndex = path.lastIndexOf('[');
    if (lastDotIndex === -1 && lastBracketIndex === -1)
        return '';
    if (lastDotIndex > lastBracketIndex)
        return path.slice(0, lastDotIndex);
    return path.slice(0, lastBracketIndex);
}
export function getFieldName(path) {
    if (!path)
        return '';
    const bracketMatch = path.match(/\[(\d+)\]$/);
    if (bracketMatch)
        return bracketMatch[1];
    const lastDotIndex = path.lastIndexOf('.');
    if (lastDotIndex === -1)
        return path;
    return path.slice(lastDotIndex + 1);
}
export function joinPath(...segments) {
    return segments
        .filter((s) => s !== '' && s !== undefined && s !== null)
        .reduce((acc, segment) => {
        const segStr = String(segment);
        if (!acc)
            return segStr;
        if (isArrayIndex(segStr))
            return `${acc}[${segStr}]`;
        if (segStr.startsWith('['))
            return `${acc}${segStr}`;
        return `${acc}.${segStr}`;
    }, '');
}
/**
 * Get all segments except the last one (parent segments).
 */
export function getParentSegments(segments) {
    return segments.slice(0, -1);
}
export function parsePath(path) {
    if (!path)
        return [];
    return path.split(/[.[\]]/).filter(Boolean);
}
export function isChildPath(childPath, parentPath) {
    if (!parentPath)
        return Boolean(childPath);
    if (!childPath)
        return false;
    return childPath.startsWith(`${parentPath}.`) || childPath.startsWith(`${parentPath}[`);
}
export function isDescendantPath(descendantPath, ancestorPath) {
    return isChildPath(descendantPath, ancestorPath);
}
/**
 * Get the last property name from a path, skipping array indices.
 * Useful for getting a display name from a path.
 *
 * Examples:
 * - "foo.bar" → "bar"
 * - "foo[0]" → "foo"
 * - "foo.bar[0]" → "bar"
 * - "execution.tools" → "tools"
 * - "items[0].name" → "name"
 */
export function getPropertyName(path) {
    if (!path)
        return '';
    const segments = parsePath(path);
    // Find the last non-numeric segment
    for (let i = segments.length - 1; i >= 0; i--) {
        if (!isArrayIndex(segments[i])) {
            return segments[i];
        }
    }
    return segments[0] ?? '';
}
/**
 * Format a field name as a display title.
 * Splits camelCase into separate words and capitalizes the first letter.
 *
 * Examples:
 * - "systemPrompt" → "System Prompt"
 * - "firstName" → "First Name"
 * - "name" → "Name"
 */
export function formatFieldTitle(fieldName) {
    if (!fieldName)
        return '';
    return fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
}
